import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, memo } from 'react';
import { List, fromJS } from 'immutable';
import { connect } from 'react-redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import { changeForm, updateStatuses, reset, validate } from '../../actions/approval/statusPopup';
import { showError, showInformation } from '../../utils/MessagePopup';
import targetStatuses from '../../utils/targetStatuses';
import { fetchTargetStatuses } from '../../actions/statuses';
import { usePopupContext } from '../../contexts/PopupContext';

/**
 * Change status popup components.
 */
const ChangeStatusPopupContainer = ({
  children,
  projectId,
  approvalId,
  status,
  approved,
  passed,
  noResponse,
  errors,
  stage,
  changeForm,
  updateStatuses,
  reset,
  validate,
  fetchTargetStatuses,
}) => {
  const { closePopup, openPopup } = usePopupContext();
  const [statuses, setStatuses] = useState(List());

  useEffect(() => {
    fetchTargetStatuses(({ response: { data } }) => {
      setStatuses(targetStatuses(fromJS(data)));
    });
  }, [fetchTargetStatuses]);

  useEffect(() => {
    if (stage === 'saved') {
      closePopup();
      showInformation(openPopup, 'Updated successfully!', () => {
        reset();
        closePopup();
      });
    } else if (stage === 'error') {
      closePopup();
      showError(openPopup, errors, () => {
        reset();
        closePopup();
      });
    }
  }, [stage, errors, closePopup, openPopup, reset]);

  const handleChange = useCallback(
    event => {
      const { name, type, checked, value } = event.target;

      changeForm(
        name,
        type.toUpperCase() === 'CHECKBOX' ? checked : name === 'status' ? parseFloat(value) : value,
      );
    },
    [changeForm],
  );

  const handleClose = useCallback(() => {
    reset();
    closePopup();
  }, [reset, closePopup]);

  const handleUpdate = useCallback(() => {
    const state = [];
    const statusText = statuses.filter(item => Number(item.get('value')) === status).getIn([0, 'text']);

    if (approved) {
      state.push('approved');
    }

    if (passed) {
      state.push('passed');
    }

    if (noResponse) {
      state.push('no response');
    }

    if (stage === 'ready') {
      updateStatuses(status, statusText, state, projectId, approvalId);
    } else {
      validate();
    }
  }, [
    status,
    stage,
    approved,
    passed,
    noResponse,
    statuses,
    updateStatuses,
    validate,
    projectId,
    approvalId,
  ]);

  const shouldShow = stage => {
    const displayingStatues = ['view', 'saving', 'validationError', 'ready'];

    return displayingStatues.indexOf(stage) >= 0;
  };

  const disabled = stage === 'saving';
  const showSpinner = stage === 'saving';

  if (shouldShow(stage)) {
    return (
      <>
        <Popups.ChangeStatusPopup
          approvalId={approvalId}
          approved={approved}
          disabled={disabled}
          errors={errors}
          noResponse={noResponse}
          onChange={handleChange}
          onClose={handleClose}
          onUpdate={handleUpdate}
          passed={passed}
          projectId={projectId}
          showSpinner={showSpinner}
          stage={stage}
          status={status}
          statuses={statuses}
        />
        {children}
      </>
    );
  }

  return null;
};

ChangeStatusPopupContainer.propTypes = {
  approvalId: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]),
  approved: PropTypes.bool.isRequired,
  changeForm: PropTypes.func.isRequired,
  children: PropTypes.node,
  errors: PropTypes.instanceOf(List).isRequired,
  fetchTargetStatuses: PropTypes.func.isRequired,
  noResponse: PropTypes.bool.isRequired,
  passed: PropTypes.bool.isRequired,
  projectId: PropTypes.number.isRequired,
  reset: PropTypes.func.isRequired,
  stage: PropTypes.string.isRequired,
  status: PropTypes.number.isRequired,
  updateStatuses: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
};

function mapStateToProps(state, props) {
  const popupState = state.approval.statusPopup;
  const { popup } = props;

  return {
    projectId: popup.getIn(['props', 'projectId']),
    approvalId: popup.getIn(['props', 'approvalId']),
    status: popupState.get('status'),
    approved: popupState.get('approved'),
    passed: popupState.get('passed'),
    noResponse: popupState.get('noResponse'),
    errors: popupState.get('errors'),
    stage: popupState.get('stage'),
  };
}

export { ChangeStatusPopupContainer };
export default connect(
  mapStateToProps,
  {
    changeForm,
    updateStatuses,
    reset,
    validate,
    fetchTargetStatuses,
  },
  mergeProps,
  connectOptions,
)(memo(ChangeStatusPopupContainer));
